import { useMemo } from 'react'
import { SLIGHTLY_OVERUTILIZED_THRESHOLD } from '@constants'
import { maxUtilizationToMemberPositionBundle } from '@editorUtils'
import { useSlabBeamToAssembly } from '@hooks'
import { groupBy, keyBy, maxBy, reject } from 'lodash-es'
import type { ShearAnchorChecksOnElementDTO } from '@api/fastAPI.schemas'
import { useAppStore } from '@stores'
import { useResultsStore, useStiffeningStore, useTransmissionStore } from '@editorStores'
import { useElementCrossSectionAssignment } from '@editorHooks'
import { getSlabBeamCrossSectionFromAssembly } from 'src/utils/assemblies'
import {
  buildMemberCheckSettingSupportsContextIndex,
  getAllowedOverutilizationPercentForCheck,
  getMemberCheckSettingSupportsContextFromIndex,
  isSupportCompressionMemberCheckType,
  type MemberCheckSettingValidationContext,
} from '../components/Results2DetailPane/utils'
import { isResults2Error, isVibrationError } from '../utils'

/**
 * Tabs whose header is tinted when any of their elements is over-utilized. Covers the primary
 * element-list tabs plus the walls tab and its sub-tabs (the walls tab is tinted when any of its
 * sub-tabs is).
 */
export type Results2ErrorTab =
  | 'beams'
  | 'purlins'
  | 'columns'
  | 'slabs'
  | 'roof-slabs'
  | 'walls'
  | 'wall-rips'
  | 'wall-lintels'
  | 'stiffening'
  | 'anchors'
  | 'shear-anchors'
  | 'standard-rips'

/**
 * Computes, per element-list tab, whether any of its elements is over-utilized (red "err" state),
 * so the tab header can be tinted like the old Results view. The active `listItems` memo only holds
 * the currently selected tab's rows, so tab colors must be derived independently from each position
 * grouping — the same way the old `GlobalTabs` did with `useMaxUtilizationOver1`, but honoring the
 * per-check over-utilization tolerances so a tab's color stays in sync with its red rows.
 *
 * `shearAnchorChecks` are the loaded shear anchor checks (`useShearAnchorChecks`); they are not in
 * the results store, so the caller passes them in.
 */
const useResults2TabErrorStatuses = (
  shearAnchorChecks: ShearAnchorChecksOnElementDTO[] | undefined,
): Record<Results2ErrorTab, boolean> => {
  const structuralChecks = useResultsStore(state => state.structuralChecks)
  const memberCheckSettings = useResultsStore(state => state.memberCheckSettings)
  const slabBeamsPositionGrouping = useResultsStore(state => state.slabBeamsPositionGrouping)
  const roofSlabBeamsPositionGrouping = useResultsStore(
    state => state.roofSlabBeamsPositionGrouping,
  )
  const beamsPositionGrouping = useResultsStore(state => state.beamsPositionGrouping)
  const purlinsPositionGrouping = useResultsStore(state => state.purlinsPositionGrouping)
  const columnsPositionGrouping = useResultsStore(state => state.columnsPositionGrouping)
  const wallRipsPositionGrouping = useResultsStore(state => state.wallRipsPositionGrouping)
  const wallLintelsPositionGrouping = useResultsStore(state => state.wallLintelsPositionGrouping)
  const stiffeningSegmentChecks = useResultsStore(state => state.stiffeningSegmentChecks)
  const standardRipChecks = useResultsStore(state => state.standardRipChecks)
  const anchorChecks = useResultsStore(state => state.anchorChecks)
  const anchorInterventionTableData = useResultsStore(state => state.anchorInterventionTableData)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  // The "Anker" tab only exists while the integrated anchor view is on (see Results2ListPane);
  // a hidden tab must not tint the Wand tab.
  const results2IntegratedAnchorView = useAppStore(state => state.results2IntegratedAnchorView)

  const elementCrossSectionAssignment = useElementCrossSectionAssignment()
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const slabBeamGuidToAssembly = useSlabBeamToAssembly()

  return useMemo(() => {
    const assignmentByGuid = keyBy(elementCrossSectionAssignment, 'element_guid')
    const memberCheckSettingsByElementGuid = keyBy(memberCheckSettings, 'element_guid')
    const supportsIndex = buildMemberCheckSettingSupportsContextIndex(verticalTransmissionGraph)
    // Index the checks by element once so per-GUID lookups don't re-scan the whole list.
    const structuralChecksByGuid = groupBy(structuralChecks ?? [], 'element_guid')

    // Same current-design-context resolution as the list row builder: assigned cross section (or the
    // slab-beam assembly's), plus the element's supports, so saved tolerances only apply when their
    // reference data still matches.
    const validationContextForGuid = (
      guid: string,
      fallbackCrossSection?: CrossSectionTimberOrCLTOrSteel | null,
    ): MemberCheckSettingValidationContext => {
      let crossSection = assignmentByGuid[guid]?.element_cs as
        | CrossSectionTimberOrCLTOrSteel
        | undefined
      if (!crossSection) {
        const assembly = slabBeamGuidToAssembly[guid]
        if (assembly) {
          try {
            crossSection = getSlabBeamCrossSectionFromAssembly(assembly)
          } catch {
            crossSection = undefined
          }
        }
      }
      return {
        crossSection: crossSection ?? fallbackCrossSection ?? undefined,
        supports: getMemberCheckSettingSupportsContextFromIndex(guid, supportsIndex),
      }
    }

    const guidHasError = (guid: string): boolean => {
      const checksForGuid = structuralChecksByGuid[guid]
      if (!checksForGuid || checksForGuid.length === 0) return false

      const supportChecks = checksForGuid.filter(check =>
        isSupportCompressionMemberCheckType(check.check_type),
      )
      const nonSupportChecks = reject(checksForGuid, check =>
        isSupportCompressionMemberCheckType(check.check_type),
      )
      const maxSupportCheck = maxBy(supportChecks, check => check.max_utilization)
      const maxNonSupportCheck = maxBy(nonSupportChecks, check => check.max_utilization)
      const maxAnyCheck = maxBy(checksForGuid, check => check.max_utilization)
      const etaSourceCheck = maxNonSupportCheck ?? maxAnyCheck

      const checkSpecificSettings = memberCheckSettingsByElementGuid[guid]?.check_specific_settings
      const context = validationContextForGuid(guid)

      return isResults2Error(
        etaSourceCheck?.max_utilization,
        maxSupportCheck?.max_utilization,
        getAllowedOverutilizationPercentForCheck(etaSourceCheck, checkSpecificSettings, context),
        getAllowedOverutilizationPercentForCheck(maxSupportCheck, checkSpecificSettings, context),
        // A failed vibration check (η = 1.0) can be outranked by a higher non-support check, so
        // scan every check rather than only the max one (kept in sync with the list row builder).
        checksForGuid.some(check => isVibrationError(check.check_type, check.max_utilization)),
      )
    }

    const groupingHasError = (grouping: MemberPositionBundle[] | null | undefined): boolean =>
      !!grouping &&
      !!structuralChecks &&
      grouping.some(bundle =>
        [bundle.representative_position, ...bundle.representative_for].some(guidHasError),
      )

    // Scheiben (stiffening) rows have no tolerance handling: a row is red when its max utilization
    // reaches the over-utilization threshold. Mirror that exactly so the tab tracks its red rows.
    const stiffeningHasError = stiffeningSegmentChecks.some(
      check => check.max_utilization >= SLIGHTLY_OVERUTILIZED_THRESHOLD,
    )

    // Std-Rippen (standard rips) rows replicate the element-list bundle/tolerance logic; the tab is
    // red when any bundle is in the red "err" state.
    const standardRipsHaveError = (): boolean => {
      if (!standardRipChecks || standardRipChecks.length === 0) return false
      const bundles = standardRipChecks.map(
        ripCheck =>
          ({
            representative_position: ripCheck.standard_rip_member.guid,
            representative_for: [],
            exported: false,
          } as MemberPositionBundle),
      )
      const checks = standardRipChecks.flatMap(ripCheck => ripCheck.standard_rip_checks)
      const bundlesWithUtil = maxUtilizationToMemberPositionBundle(bundles, checks)

      return bundlesWithUtil.some(bundle => {
        const guid = bundle.representative_position
        const checksForRip = standardRipChecks.filter(
          ripCheck => ripCheck.standard_rip_member.guid === guid,
        )
        const ripCheck = checksForRip.length === 1 ? checksForRip[0] : undefined
        const checkSpecificSettings =
          memberCheckSettingsByElementGuid[guid]?.check_specific_settings
        const context = validationContextForGuid(guid, ripCheck?.standard_rip_member.cross_section)

        return isResults2Error(
          bundle.maxUtilWithoutSupportCompression?.max_utilization,
          bundle.maxUtilOnlySupportCompression?.max_utilization,
          getAllowedOverutilizationPercentForCheck(
            bundle.maxUtilWithoutSupportCompression,
            checkSpecificSettings,
            context,
          ),
          getAllowedOverutilizationPercentForCheck(
            bundle.maxUtilOnlySupportCompression,
            checkSpecificSettings,
            context,
          ),
          isVibrationError(
            bundle.maxUtilWithoutSupportCompression?.check_type,
            bundle.maxUtilWithoutSupportCompression?.max_utilization,
          ),
        )
      })
    }

    // Anker rows (one per anchor of the intervention table) are red when their check's
    // utilization reaches the over-utilization threshold; anchors without a check show η = 0.
    const anchorCheckBySupport = keyBy(anchorChecks ?? [], 'element_guid')
    const anchorsHaveError =
      results2IntegratedAnchorView &&
      (anchorInterventionTableData ?? []).some(
        row =>
          (anchorCheckBySupport[row.support_guid]?.utilization ?? 0) >=
          SLIGHTLY_OVERUTILIZED_THRESHOLD,
      )

    // Schubanker rows exist per stiffening segment (the node's wall-level entries are not listed)
    // and per position; a row is red when its decisive check reaches the threshold.
    const segmentGuids = new Set(
      (stiffeningIntervals ?? []).map(segment => segment.guid).filter(Boolean),
    )
    const shearAnchorsHaveError = (shearAnchorChecks ?? []).some(
      entry =>
        segmentGuids.has(entry.element_guid) &&
        entry.checks.some(check => check.utilization >= SLIGHTLY_OVERUTILIZED_THRESHOLD),
    )

    const wallRipsError = groupingHasError(wallRipsPositionGrouping)
    const wallLintelsError = groupingHasError(wallLintelsPositionGrouping)
    const standardRipsError = standardRipsHaveError()

    return {
      beams: groupingHasError(beamsPositionGrouping),
      purlins: groupingHasError(purlinsPositionGrouping),
      columns: groupingHasError(columnsPositionGrouping),
      slabs: groupingHasError(slabBeamsPositionGrouping),
      'roof-slabs': groupingHasError(roofSlabBeamsPositionGrouping),
      'wall-rips': wallRipsError,
      'wall-lintels': wallLintelsError,
      stiffening: stiffeningHasError,
      anchors: anchorsHaveError,
      'shear-anchors': shearAnchorsHaveError,
      'standard-rips': standardRipsError,
      // The primary Wand tab is red when any of its sub-tabs is (Wände carries no checks).
      walls:
        wallRipsError ||
        wallLintelsError ||
        stiffeningHasError ||
        anchorsHaveError ||
        shearAnchorsHaveError ||
        standardRipsError,
    }
  }, [
    structuralChecks,
    memberCheckSettings,
    anchorChecks,
    anchorInterventionTableData,
    stiffeningIntervals,
    shearAnchorChecks,
    results2IntegratedAnchorView,
    beamsPositionGrouping,
    purlinsPositionGrouping,
    columnsPositionGrouping,
    slabBeamsPositionGrouping,
    roofSlabBeamsPositionGrouping,
    wallRipsPositionGrouping,
    wallLintelsPositionGrouping,
    stiffeningSegmentChecks,
    standardRipChecks,
    elementCrossSectionAssignment,
    verticalTransmissionGraph,
    slabBeamGuidToAssembly,
  ])
}

export default useResults2TabErrorStatuses
